import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, CreditCard } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

import SubscriptionPlans, {
  SubscriptionPlan,
} from "@/components/subscription/SubscriptionPlans";
import SubscriptionStatus from "@/components/subscription/SubscriptionStatus";
import BillingHistory from "@/components/subscription/BillingHistory";
import SubscriptionModal from "@/components/subscription/SubscriptionModal";
import CancelSubscriptionDialog from "@/components/subscription/CancelSubscriptionDialog";
import { EventsService } from "@/api/events";
import {
  getCurrentClientData,
  updateLocalStorageField,
} from "@/services/authServices";

const Billing = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("plans");
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const loggedInUser = getCurrentClientData();
  const [paymentSecret, setPaymentSecret] = useState("");

  const { paymentPro, subscriptionType } = EventsService;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [paymentScreen, setPaymentScreen] = useState(false);

  // Mock user data - in a real app, this would come from your user context or API
  const [userPlan, setUserPlan] = useState<SubscriptionPlan>(plans[0]);
  const [currentplan, setCurrentPlan] = useState<SubscriptionPlan[]>(plans);
  const [renewalDate, setRenewalDate] = useState<string | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<
    { type: string; last4?: string; expiryDate?: string } | undefined
  >(undefined);

  // Mock billing history - in a real app, this would come from your API
  const [transactions, setTransactions] = useState([
    /**
    {
      id: "inv-001",
      date: "2023-04-01",
      description: "Pro Plan Subscription",
      amount: 19.99,
      status: "paid" as const,
    },
    {
      id: "inv-002",
      date: "2023-03-01",
      description: "Pro Plan Subscription",
      amount: 19.99,
      status: "paid" as const,
    },
     */
  ]);

  const fetchSubscriptionData = async () => {
    try {
      const response: any = await subscriptionType(loggedInUser.token);
      if (response.statusCode === 200) {
        const { account_type, status, valid_until } =
          response.body.data.account_type;
        setUserPlan(plans.find((plan) => plan.id === account_type));
        setRenewalDate(valid_until);
        updateLocalStorageField({
          account_type: "pro",
        });

        // setLoggedInUser((prevUser) => ({
        //   ...prevUser,
        //   account_type,
        //   status,
        //   valid_until,
        // }));
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    }
  };
  const handleSelectPlan = (planId: SubscriptionPlan) => {
    const plan = plans.find((p) => p.id === planId.id);
    if (plan) {
      setSelectedPlan(plan);
      setIsModalOpen(true);
    }
  };

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    setIsModalOpen(false);
    setPaymentScreen(false);
    fetchSubscriptionData();
    // In a real app, you would call your API to update the subscription
    setUserPlan(plan);

    // Set mock renewal date and payment method for paid plans
    if (typeof plan.price === "number") {
      const today = new Date();
      if (plan.billingPeriod === "monthly") {
        const nextMonth = new Date(today.setMonth(today.getMonth() + 1));
        setRenewalDate(nextMonth.toLocaleDateString());
      } else if (plan.billingPeriod === "yearly") {
        const nextYear = new Date(today.setFullYear(today.getFullYear() + 1));
        setRenewalDate(nextYear.toLocaleDateString());
      }

      setPaymentMethod({
        type: "Visa",
        last4: "4242",
        expiryDate: "03/25",
      });

      // Add transaction to billing history
      const newTransaction = {
        id: `inv-${transactions.length + 1}`,
        date: new Date().toLocaleDateString(),
        description: `${plan.name} Plan Subscription`,
        amount: plan.price as number,
        status: "paid" as const,
      };

      // const payLoad: PaymentPro = {
      //   account_type: plan.id,
      //   subscription_type: plan.billingPeriod,
      //   subscription_price: plan.price.toString(),
      //   months_valid: "1",
      //   payment_method_id: "pm_card_visa",
      // };
    } else {
      // Reset for free plan
      setRenewalDate(undefined);
      setPaymentMethod(undefined);
    }
  };

  const handleCancelSubscription = () => {
    // In a real app, you would call your API to cancel the subscription
    setUserPlan(plans[0]); // Set back to Free plan
    setRenewalDate(undefined);
    setPaymentMethod(undefined);
    setIsCancelDialogOpen(false);

    toast({
      title: "Subscription Cancelled",
      description: "Your subscription has been cancelled successfully.",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center mb-6">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="mr-2">
              <ChevronLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <SubscriptionStatus
              currentPlan={currentplan}
              renewalDate={renewalDate}
              paymentMethod={paymentMethod}
              accountType={loggedInUser.account_type}
              onManageSubscription={() => setActiveTab("plans")}
              onCancelSubscription={() => setIsCancelDialogOpen(true)}
            />
          </div>

          <div className="md:col-span-2">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="mb-6">
                <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
                {/* <TabsTrigger value="history">Billing History</TabsTrigger> */}
              </TabsList>

              <TabsContent value="plans">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold mb-2">
                    Choose Your Plan
                  </h2>
                  <p className="text-muted-foreground">
                    Select the plan that best fits your event management needs.
                  </p>
                </div>

                <SubscriptionPlans
                  plans={plans}
                  setPaymentScreen={setPaymentScreen}
                  currentPlanId={userPlan?.id}
                  accountType={loggedInUser.account_type}
                  setPaymentSecret={setPaymentSecret}
                  onSelectPlan={handleSelectPlan}
                />
              </TabsContent>
              {/* 
              <TabsContent value="history">
                <BillingHistory transactions={transactions} />
              </TabsContent> */}
            </Tabs>
          </div>
        </div>

        <SubscriptionModal
          isOpen={isModalOpen}
          setPaymentScreen={setPaymentScreen}
          onClose={() => setIsModalOpen(false)}
          selectedPlan={selectedPlan}
          paymentSecret={paymentSecret}
          onSubscribe={handleSubscribe}
        />

        <CancelSubscriptionDialog
          isOpen={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          onConfirm={handleCancelSubscription}
          planName={userPlan?.name}
        />
      </div>
    </div>
  );
};

// Mock subscription plans
const plans: SubscriptionPlan[] = [
  {
    id: "standard",
    name: "Standard",
    description: "For individuals and small events",
    price: "Free",
    billingPeriod: "monthly",
    features: [
      { name: "Up to 3 events per month", included: true },
      { name: "Basic event management", included: true },
      { name: "Up to 50 attendees per event", included: true },
      { name: "Email support", included: true },
      { name: "Custom branding", included: false },
      { name: "Advanced analytics", included: false },
      { name: "Priority support", included: false },
      { name: "Unlimited team members", included: false },
    ],
    buttonText: "Get Started",
  },
  {
    id: "pro",
    name: "Pro",
    description: "For growing businesses and regular events",
    price: 0.99,
    billingPeriod: "monthly",
    features: [
      { name: "Unlimited events", included: true, highlight: true },
      { name: "Advanced event management", included: true },
      { name: "Up to 500 attendees per event", included: true },
      { name: "Email + chat support", included: true },
      { name: "Custom branding", included: true },
      { name: "Basic analytics", included: true },
      { name: "3 team members", included: true },
      { name: "Priority support", included: false },
    ],
    popular: true,
    buttonText: "Upgrade to Pro",
  },
  {
    id: "business",
    name: "Business",
    description: "For large organizations and frequent events",
    price: 1,
    billingPeriod: "monthly",
    features: [
      { name: "Unlimited events", included: true },
      { name: "Premium event management", included: true },
      { name: "Unlimited attendees", included: true, highlight: true },
      { name: "24/7 priority support", included: true },
      { name: "Custom branding", included: true },
      { name: "Advanced analytics & reporting", included: true },
      { name: "Unlimited team members", included: true },
      { name: "API access", included: true },
    ],
    buttonText: "Upgrade to Business",
  },
];

export default Billing;
